/**
 * Pinia Stores 统一入口和管理中心
 */

import { useCacheStore } from './cache'
import { useAuthStore, useUserStore, useRanksStore, useSkinsStore, useGunSkinsStore, useOperatorSkinsStore, useAccountsStore, usePaymentsStore, useWalletStore, useNotificationsStore } from './stores'
import { cacheInitConfig } from './config'

// ==================== 导出所有stores ====================

export {
    useCacheStore,
    useAuthStore,
    useUserStore,
    useRanksStore,
    useSkinsStore,
    useGunSkinsStore,
    useOperatorSkinsStore,
    useAccountsStore,
    usePaymentsStore,
    useWalletStore,
    useNotificationsStore
}

// ==================== 全局stores管理 ====================

/**
 * 初始化所有stores
 */
export const initStores = async (): Promise<void> => {
    // 初始化缓存store
    const cacheStore = useCacheStore()
    cacheStore.init(cacheInitConfig)
    // 立即持久化一次，确保倒计时（如验证码）被保存
    cacheStore.persistCache(cacheInitConfig.persistKeys, cacheInitConfig.storageKey)

    // 初始化认证store
    const authStore = useAuthStore()
    await authStore.initAuth()

    // 初始化用户store
    const userStore = useUserStore()
    userStore.initUser()
}

/**
 * 清除所有stores数据
 */
export const clearAllStores = async (): Promise<void> => {
    try {
        // 1. 清除认证信息（包含用户数据和token）
        const authStore = useAuthStore()
        authStore.clearUserAuth()

        // 2. 清除所有缓存（这会清除其他业务数据缓存）
        const cacheStore = useCacheStore()
        cacheStore.clearAllCache()
    } catch (error) {
        console.error('❌ 清除stores数据失败:', error)
        throw error
    }
}

/**
 * 获取stores状态信息（调试用）
 */
export const getStoresStatus = () => {
    const cacheStore = useCacheStore()
    const authStore = useAuthStore()
    const userStore = useUserStore()
    const ranksStore = useRanksStore()
    const skinsStore = useSkinsStore()
    const operatorSkinsStore = useOperatorSkinsStore()

    return {
        timestamp: new Date().toISOString(),
        cache: {
            stats: cacheStore.cacheStats,
            totalItems: cacheStore.cache.size
        },
        auth: {
            isAuthenticated: authStore.isAuthenticated,
            hasUser: !!authStore.currentUser,
            userDisplayName: authStore.userDisplayName
        },
        user: {
            isProfileComplete: userStore.isProfileComplete,
            hasSettings: Object.keys(userStore.userSettings).length > 0,
            operationLoading: userStore.userOperationLoading
        },
        config: {
            ranks: {
                count: ranksStore.rankCount,
                hasData: ranksStore.hasRankList,
                loading: ranksStore.rankListLoading
            },
            skins: {
                count: skinsStore.skinCount,
                hasData: skinsStore.hasSkinList,
                loading: skinsStore.skinListLoading
            },
            operatorSkins: {
                count: operatorSkinsStore.operatorSkinCount,
                hasData: operatorSkinsStore.hasOperatorSkinList,
                loading: operatorSkinsStore.operatorSkinListLoading
            }
        }
    }
}

// 默认导出初始化函数
export default initStores

// 导出配置
export { cacheInitConfig } from './config'

// 导出管理员 stores
export * from './stores_admin'
